import {
  Megaphone,
  MessageCircle,
  PackageCheck,
  ShieldCheck,
  TrendingDown,
  type LucideIcon,
} from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import type { AppNotification, NotificationType } from "@/features/engagement/types/notification";
import { cn } from "@/utils/cn";
import { formatRelativeTime } from "@/utils/formatRelativeTime";

const TYPE_ICON: Record<NotificationType, LucideIcon> = {
  MESSAGE: MessageCircle,
  PRICE_DROP: TrendingDown,
  PRODUCT_SOLD: PackageCheck,
  PRODUCT_APPROVED: ShieldCheck,
  ACCOUNT: ShieldCheck,
  SYSTEM: Megaphone,
};

const TYPE_COLOR: Record<NotificationType, string> = {
  MESSAGE: "bg-info/10 text-info",
  PRICE_DROP: "bg-success/10 text-success",
  PRODUCT_SOLD: "bg-warning/10 text-warning",
  PRODUCT_APPROVED: "bg-success/10 text-success",
  ACCOUNT: "bg-info/10 text-info",
  SYSTEM: "bg-muted text-muted-foreground",
};

interface NotificationListItemProps {
  notification: AppNotification;
  onClick: () => void;
}

/** docs/ui/component-inventory.md's `NotificationListItem`/`NotificationCard`:
 * one sub-variant per type, each pairing a distinct icon with the matching
 * semantic color. Unread renders bold with an accent dot; there's no
 * separate "read" styling beyond the absence of that. */
export function NotificationListItem({ notification, onClick }: NotificationListItemProps) {
  const Icon = TYPE_ICON[notification.type];

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left hover:bg-muted"
    >
      <span
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
          TYPE_COLOR[notification.type],
        )}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className={cn("text-body", notification.isRead ? "font-medium" : "font-semibold")}>
            {notification.title}
          </p>
          {!notification.isRead && (
            <span
              className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary"
              aria-label="Unread"
            />
          )}
        </div>
        <p className="truncate text-body-sm text-muted-foreground">{notification.body}</p>
        <p className="mt-0.5 text-caption text-muted-foreground">
          {formatRelativeTime(notification.createdAt)}
        </p>
      </div>
    </button>
  );
}

export function NotificationListItemSkeleton() {
  return (
    <div className="flex items-start gap-3 border-b border-border px-4 py-3">
      <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  );
}
